import { Input } from 'antd'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import shallow from 'zustand/shallow'
import { useStore } from '../../../store/store'
import { variableLockKeysInBox } from '../../../collab/variableRefs'
import { mutateInjectVariablesInDocument } from '../../../utils/variables'
import { pageBackground } from '../../../constants/constants'
import { mutateSearchAndLaunchTrigger } from '../../../utils/triggers'
import { registerActiveField } from '../../../utils/activeFields'

// Mémoriser le selector pour éviter sa recréation à chaque render
const createSelector = (variableKey, updateVariableFn) => ({
  variables,
  updateVariable,
}) => {
  return {
    variables,
    variable: variables[variableKey],
    updateVariable: updateVariableFn ?? updateVariable
  }
}

const CustomTextVariable = ({ variableKey, updateVariableFn, isBoxSimulated = false, variableValue }) => {
  // Mémoriser le selector pour éviter sa recréation
  const memoizedSelector = useMemo(
    () => createSelector(variableKey, updateVariableFn),
    [variableKey, updateVariableFn]
  )
  
  const { variable, updateVariable, variables } = useStore(memoizedSelector)

  // Collab : input désactivé si la variable est verrouillée par un co-éditeur,
  // ou si une box verrouillée par un co-éditeur embarque cette variable
  const collabLockState = useStore(
    ({ boxLocks, boxes, user }) => ({ boxLocks, boxes, userId: user?.id }),
    shallow
  )
  const remoteLock = useMemo(() => {
    const { boxLocks, boxes, userId } = collabLockState;
    const lockedIds = Object.keys(boxLocks);
    if (lockedIds.length === 0) return null;
    const direct = boxLocks[variableKey];
    if (direct && direct.id !== userId) return direct;
    const blockingId = lockedIds.find((lockedId) => {
      if (boxLocks[lockedId].id === userId) return false;
      const lockedBox = boxes.find((b) => b.id === lockedId);
      return lockedBox && variableLockKeysInBox(lockedBox).includes(variableKey);
    });
    return blockingId ? boxLocks[blockingId] : null;
  }, [collabLockState, variableKey])
  
  // Réduire les appels useStore à un seul selector
  const { 
    isTemplate, 
    searchResults, 
    searchIndex, 
    searchUiHasFocus, 
    setSearchSelectionActive,
    handleBoxVariableSelectedId,
    ignoreVariableSaveKeys,
    removeIgnoreVariableSaveKey,
    searchSelectionActive
  } = useStore(({ 
    currentDocument, 
    searchResults, 
    searchIndex, 
    searchUiHasFocus, 
    setSearchSelectionActive,
    handleBoxVariableSelectedId,
    ignoreVariableSaveKeys,
    removeIgnoreVariableSaveKey,
    searchSelectionActive
  }) => ({
    isTemplate: currentDocument.type === 'template',
    searchResults,
    searchIndex,
    searchUiHasFocus,
    setSearchSelectionActive,
    handleBoxVariableSelectedId,
    ignoreVariableSaveKeys,
    removeIgnoreVariableSaveKey,
    searchSelectionActive
  }))
  
  const [value, setValue] = useState(variableValue ?? variable.value)
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef()

  // Mémoriser activeRange pour éviter le recalcul à chaque render
  const activeRange = useMemo(() => {
    const r = searchResults[searchIndex]
    if (r && r.boxId === variableKey && r.start != null && r.end != null) {
      return { start: r.start, end: r.end }
    }
    return null
  }, [searchResults, searchIndex, variableKey])

  useEffect(() => {
    const isIgnored = ignoreVariableSaveKeys?.includes(variableKey)
    if (isIgnored || searchSelectionActive) return
    setValue(variableValue ?? variable.value)
  }, [variableValue, variable.value, variableKey, ignoreVariableSaveKeys, searchSelectionActive])

  const save = useCallback((val) => {
    if (isTemplate && !isBoxSimulated) return
    const ignoreKeys = ignoreVariableSaveKeys || []
    if (ignoreKeys.includes(variableKey)) return
    updateVariable(variableKey, (variable, draft) => {
      variable.value = val ?? '';
      if (!isBoxSimulated) {
        mutateSearchAndLaunchTrigger({ triggerId: variableKey, draft });
        mutateInjectVariablesInDocument({
          variables,
          variable,
          variableKey,
          draft
        })
      }
    })
  }, [isBoxSimulated, isTemplate, updateVariable, variableKey, variables, ignoreVariableSaveKeys])

  const cleanupRef = useRef(value);

  useEffect(() => {
    cleanupRef.current = value
  });

  // Insertion d'une valeur prédéfinie : le bouton qui la déclenche est rendu
  // dans le libellé de la ligne, il n'a aucun moyen de connaître le curseur.
  // Champ jamais focalisé : insertion en fin de texte.
  useEffect(() => {
    if (isBoxSimulated) return undefined
    return registerActiveField(variableKey, {
      insertAtCursor: (text) => {
        if (typeof text !== 'string' || text === '') return
        const textArea = ref?.current?.resizableTextArea?.textArea
        const current = cleanupRef.current ?? ''
        const start = textArea?.selectionStart ?? current.length
        const end = textArea?.selectionEnd ?? current.length
        const nextValue = `${current.slice(0, start)}${text}${current.slice(end)}`
        setValue(nextValue)
        save(nextValue)
        const caret = start + text.length
        // Après le rendu de la nouvelle valeur, sinon le navigateur repose le
        // curseur en fin de champ.
        requestAnimationFrame(() => {
          try {
            textArea?.focus()
            textArea?.setSelectionRange(caret, caret)
          } catch (e) {
            // Champ démonté entre-temps.
          }
        })
      },
    })
  }, [variableKey, isBoxSimulated, save])

  useLayoutEffect(() => {
    return () => {
      const isIgnored = ignoreVariableSaveKeys?.includes(variableKey)
      if (isFocused && !isIgnored && !searchSelectionActive && cleanupRef.current !== undefined) {
        save(cleanupRef.current)
      }
    };
  }, [isFocused, variableKey, save, ignoreVariableSaveKeys, searchSelectionActive]);

  // Appliquer la sélection sur l'occurrence active et faire défiler si besoin
  useEffect(() => {
    const active = activeRange
    const ta = ref?.current?.resizableTextArea?.textArea
    if (!ta || !active) return
    if (searchUiHasFocus) return
    try {
      ta.focus()
      ta.setSelectionRange(active.start, active.end)
    } catch (e) {
      // ignore
    }
  }, [activeRange, searchUiHasFocus])

  useEffect(() => {
    if (!activeRange) setSearchSelectionActive(false)
  }, [activeRange, setSearchSelectionActive])

  // Mémoriser les callbacks pour éviter leur recréation à chaque render
  const handleClick = useCallback(() => {
    ref.current?.focus()
  }, [])

  const handleChange = useCallback((e) => {
    if (searchSelectionActive) {
      e.preventDefault();
      return;
    }
    setValue(e.currentTarget.value ?? '')
  }, [searchSelectionActive])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
    // Toute action sur un champ rend le container/variable actif
    // Appel direct pour une réactivité immédiate
    // handleBoxVariableSelectedId est déjà optimisé dans le store
    try {
      handleBoxVariableSelectedId(variableKey)
    } catch (e) { }
  }, [handleBoxVariableSelectedId, variableKey])

  const handleBlur = useCallback((e) => {
    setIsFocused(false)
    if (ignoreVariableSaveKeys?.includes(variableKey)) {
      removeIgnoreVariableSaveKey(variableKey)
      setSearchSelectionActive(false)
      return
    }
    save(e.target.value)
    setSearchSelectionActive(false)
  }, [ignoreVariableSaveKeys, variableKey, removeIgnoreVariableSaveKey, setSearchSelectionActive, save])

  const handleKeyDown = useCallback((e) => {
    if (searchSelectionActive && (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
    }
  }, [searchSelectionActive])

  return (
    <div style={{ position: 'relative', padding: "8px 16px", marginTop: 'auto' }} className='variable-selection'>
      {/* Couleur de sélection orange pour le textarea */}
      <style>{`.variable-selection textarea::selection { background: rgba(255,165,0,0.5); }`}</style>
      <Input.TextArea
        value={value}
        data-role={pageBackground}
        ref={ref}
        onClick={handleClick}
        onChange={handleChange}
        placeholder={remoteLock ? `Édité par ${remoteLock.name}...` : "Insérer la valeur..."}
        disabled={(!isBoxSimulated && isTemplate) || Boolean(remoteLock)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{ position: 'relative', zIndex: 1 }}
      />
    </div>
  )
}

// Mémoriser le composant pour éviter les re-renders inutiles
// Comparaison personnalisée pour gérer les props optionnelles correctement
export default React.memo(CustomTextVariable, (prevProps, nextProps) => {
  // Comparer toutes les props importantes
  return (
    prevProps.variableKey === nextProps.variableKey &&
    prevProps.isBoxSimulated === nextProps.isBoxSimulated &&
    prevProps.variableValue === nextProps.variableValue &&
    // Pour updateVariableFn, comparer les références (si undefined, c'est OK)
    (prevProps.updateVariableFn === nextProps.updateVariableFn || 
     (!prevProps.updateVariableFn && !nextProps.updateVariableFn))
  )
})
